import { useMemo, useState, type MouseEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { customerDisplayName, type CustomerListItem } from '@technohull/shared';
import { useAuth } from '../auth/AuthContext.js';
import {
  useArchiveCustomer,
  useCustomer,
  useCustomers,
  useDuplicates,
  useRestoreCustomer,
  useUnmergeCustomer,
} from '../features/customers.js';
import { useNameResolver } from '../features/directory.js';
import { CustomerFormModal } from '../components/CustomerFormModal.js';
import {
  EmptyState,
  ErrorState,
  FilterBar,
  FilterSelect,
  FilterToggle,
  InlineError,
  LoadingState,
  Modal,
  PageHeader,
  Pill,
  SearchInput,
  useConfirm,
} from '../components/ui.js';
import { useFormatDate } from '../lib/format.js';
import { useIsPhone } from '../lib/media.js';
import { fold, hasText } from '../lib/search.js';
import { IconAlertTriangle } from '../components/icons.js';

/** a dialable tel: link from a typed number ("+30 210 555 4410" → tel:+302105554410) */
const telHref = (phone: string) => `tel:${phone.replace(/[^\d+]/g, '')}`;

// ── detail modal ────────────────────────────────────────────────────────────────
function DetailModal({ id, onClose, onEdit }: { id: number; onClose: () => void; onEdit: () => void }) {
  const { data, isLoading, error } = useCustomer(id);
  const { can } = useAuth();
  const nameOf = useNameResolver();
  const unmerge = useUnmergeCustomer();
  const fmtDate = useFormatDate();
  const navigate = useNavigate();
  const [confirmNode, confirm] = useConfirm();
  const row = (k: string, v: string | number | null | undefined, href?: string) =>
    v ? (<div className="cfg-sum-row"><span>{k}</span>{href ? <a href={href}>{v}</a> : <span>{v}</span>}</div>) : null;
  // open a related order in Sales Orders. On touch the dialog owns a history entry (back-to-close), so the
  // order list REPLACES it and the dialog closes once the router has moved: Back then returns here
  // without a stray duplicate entry. Elsewhere it is the plain close-then-navigate.
  const openOrder = (orderNumber: string) => (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return; // new tab / window
    e.preventDefault();
    const path = `/sales/orders?q=${encodeURIComponent(orderNumber)}`;
    const owned = (window.history.state as { __overlay?: number } | null)?.__overlay != null;
    if (!owned) { onClose(); navigate(path); return; }
    void Promise.resolve(navigate(path, { replace: true })).finally(onClose);
  };

  return (
    <>
      <Modal
        title={data ? customerDisplayName(data) : 'Customer'}
        dataTitle={!!data}
        width={640}
        onClose={onClose}
        footer={
          <>
            <button className="btn btn-ghost" onClick={onClose}>Close</button>
            {can('customer_database.edit') && data?.status === 'active' && (
              <button className="btn btn-primary" onClick={onEdit}>Edit</button>
            )}
          </>
        }
      >
        {isLoading && <LoadingState />}
        {error && <ErrorState error={error} />}
        {data && (
          <div className="cust-detail">
            <div className="cfg-sum-row"><span>Type</span><span style={{ textTransform: 'capitalize' }}>{data.customerType}{data.status !== 'active' && <>{' '}<Pill kind="warn">{data.status}</Pill></>}</span></div>
            {row('Company', data.companyName)}
            {row('Contact', `${data.firstName} ${data.lastName}`.trim())}
            {row('Email', data.email, data.email ? `mailto:${data.email}` : undefined)}
            {row('Telephone', data.telephone, data.telephone ? telHref(data.telephone) : undefined)}
            {row('Mobile', data.mobile, data.mobile ? telHref(data.mobile) : undefined)}
            {row('VAT', data.vatNumber)}
            {row('Tax code', data.taxCode)}
            {row('Profession', data.profession)}
            {row('Dealer', data.dealer)}
            {row('Registered address', data.registeredAddress)}
            {row('Street', [data.street, data.postalCode, data.city].filter(Boolean).join(', '))}
            {row('Country', data.country)}
            {row('Notes', data.notes)}

            <div className="cfg-cat-hd" style={{ marginTop: 14 }}>Related orders ({data.orders.length})</div>
            {data.orders.length === 0 && <div className="muted">No linked orders.</div>}
            {data.orders.map((o) => (
              <div key={o.orderId} className="ov-row">
                <Link to={`/sales/orders?q=${encodeURIComponent(o.orderNumber)}`} onClick={openOrder(o.orderNumber)}>
                  {o.orderNumber} · {o.modelName || '-'} · {o.vesselName || '-'} {o.sapNumber ? `· ${o.sapNumber}` : ''}
                </Link>
                <span className="muted">{o.status} · {fmtDate(o.confirmedAt || o.createdAt || '')}</span>
              </div>
            ))}
            {data.models.length > 0 && (
              <div style={{ marginTop: 8, fontSize: 12 }}><span className="muted">Models purchased: </span>{data.models.join(', ')}</div>
            )}

            {data.mergeHistory.length > 0 && (
              <>
                <div className="cfg-cat-hd" style={{ marginTop: 14 }}>Merge history</div>
                {unmerge.isError && <InlineError error={unmerge.error} onDismiss={() => unmerge.reset()} />}
                {data.mergeHistory.map((m) => (
                  <div key={m.id} className="ov-row" style={{ fontSize: 12 }}>
                    <span className="muted">
                      #{m.mergedCustomerId} → #{m.primaryCustomerId} by {nameOf(m.mergedBy)} · {fmtDate(m.createdAt)}
                      {m.reversedAt ? <>{' '}<Pill kind="info">reversed</Pill></> : null}
                    </span>
                    {!m.reversedAt && can('customer_database.merge') && (
                      <button
                        className="btn btn-sm btn-ghost cust-undo"
                        disabled={unmerge.isPending}
                        onClick={async () => {
                          if (await confirm({
                            title: 'Undo merge',
                            message: `Restore #${m.mergedCustomerId} as a separate customer and move its orders back from #${m.primaryCustomerId}?`,
                            confirmLabel: 'Undo merge',
                            danger: true,
                          })) unmerge.mutate(m.id);
                        }}
                      >Undo merge</button>
                    )}
                  </div>
                ))}
              </>
            )}

            <div className="muted" style={{ fontSize: 11, marginTop: 14 }}>
              Created by {nameOf(data.createdBy)} · {fmtDate(data.createdAt)} · last updated {nameOf(data.updatedBy)} · {fmtDate(data.updatedAt)}
            </div>
          </div>
        )}
      </Modal>
      {/* a sibling, not inside the dialog, so its Escape / scrim click closes only the confirm */}
      {confirmNode}
    </>
  );
}

// ── page ──────────────────────────────────────────────────────────────────────
export function CustomerDatabase() {
  const { can } = useAuth();
  const [includeArchived, setIncludeArchived] = useState(false);
  const { data, isLoading, isFetching, error, refetch } = useCustomers(includeArchived);
  const duplicates = useDuplicates();
  const archive = useArchiveCustomer();
  const restore = useRestoreCustomer();
  const fmtDate = useFormatDate();
  const dupCount = duplicates.data?.candidates.length ?? 0;
  const isPhone = useIsPhone(); // the search placeholder must fit beside "Filters" on a phone

  const [q, setQ] = useState('');
  const [typeF, setTypeF] = useState('');
  const [countryF, setCountryF] = useState('');
  const [dealerF, setDealerF] = useState('');
  const [hasOrders, setHasOrders] = useState(false);
  const [dupOnly, setDupOnly] = useState(false);

  const [detailId, setDetailId] = useState<number | null>(null);
  const [editId, setEditId] = useState<number | null | undefined>(undefined); // undefined=closed, null=new

  const all = data?.customers ?? [];
  const countries = useMemo(() => [...new Set(all.map((c) => c.country).filter(Boolean))].sort(), [all]);
  const dealers = useMemo(() => [...new Set(all.map((c) => c.dealer).filter(Boolean))].sort(), [all]);

  const rows = useMemo(() => {
    const query = fold(q.trim());
    return all.filter((c) => {
      if (typeF && c.customerType !== typeF) return false;
      if (countryF && c.country !== countryF) return false;
      if (dealerF && c.dealer !== dealerF) return false;
      if (hasOrders && c.orderCount === 0) return false;
      if (dupOnly && c.duplicateCount === 0) return false;
      if (query) {
        const hay = [customerDisplayName(c), c.companyName, c.vatNumber, c.email, c.telephone, c.mobile, c.country, c.dealer].join(' ');
        if (!hasText(query, hay)) return false;
      }
      return true;
    });
  }, [all, q, typeF, countryF, dealerF, hasOrders, dupOnly]);

  const activeFilters = [typeF, countryF, dealerF, hasOrders, dupOnly, includeArchived].filter(Boolean).length;
  const clearFilters = () => {
    setTypeF(''); setCountryF(''); setDealerF('');
    setHasOrders(false); setDupOnly(false); setIncludeArchived(false);
  };

  const editingInitial = useMemo<(CustomerListItem & { id: number }) | undefined>(
    () => (typeof editId === 'number' ? all.find((c) => c.id === editId) : undefined),
    [editId, all],
  );

  return (
    <>
      <PageHeader title="Customer Database">
        <span className="muted page-count">{rows.length} customer{rows.length === 1 ? '' : 's'}</span>
        <Link to="/sales/customers/duplicates" className="btn btn-sm"><IconAlertTriangle size={14} /> Duplicates{dupCount ? ` (${dupCount})` : ''}</Link>
        {can('customer_database.create') && <button className="btn btn-sm btn-primary" onClick={() => setEditId(null)}>+ Customer</button>}
        <button className="btn btn-sm" disabled={isFetching} onClick={() => void refetch()}>{isFetching ? 'Refreshing…' : 'Refresh'}</button>
      </PageHeader>
      <FilterBar
        search={<SearchInput value={q} onChange={setQ} placeholder={isPhone ? 'Search customers' : 'Search name / company / VAT / email / phone'} width={300} />}
        activeCount={activeFilters}
        onClear={clearFilters}
      >
        <FilterSelect label="Type" value={typeF} onChange={setTypeF} ariaLabel="Customer type"
          options={[{ value: '', label: 'All types' }, { value: 'individual', label: 'Individual' }, { value: 'company', label: 'Company' }]} />
        <FilterSelect label="Country" value={countryF} onChange={setCountryF}
          options={[{ value: '', label: 'All countries' }, ...countries.map((c) => ({ value: c, label: c }))]} />
        <FilterSelect label="Dealer" value={dealerF} onChange={setDealerF}
          options={[{ value: '', label: 'All dealers' }, ...dealers.map((d) => ({ value: d, label: d }))]} />
        <FilterToggle checked={hasOrders} onChange={setHasOrders}>Has orders</FilterToggle>
        <FilterToggle checked={dupOnly} onChange={setDupOnly}>Possible duplicate</FilterToggle>
        <FilterToggle checked={includeArchived} onChange={setIncludeArchived}>Show archived</FilterToggle>
      </FilterBar>
      {(archive.error ?? restore.error) && (
        <InlineError error={archive.error ?? restore.error} onDismiss={() => { archive.reset(); restore.reset(); }} />
      )}

      {isLoading && <LoadingState label="Loading customers…" />}
      {error && <ErrorState error={error} onRetry={() => void refetch()} />}
      {data && rows.length === 0 && <EmptyState title="No customers" hint={all.length ? 'No matches for these filters.' : 'Customers are created from the Configurator or with “+ Customer”.'} />}

      {data && rows.length > 0 && (
        <div className="tbl-wrap">
          <table className="tbl tbl-cards">
            <thead>
              <tr>
                <th>Customer</th><th>Company</th><th>VAT</th><th>Email</th><th>Telephone</th>
                <th>Country</th><th>Dealer</th><th>Orders</th><th>Last order</th><th>Updated</th><th></th><th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const name = customerDisplayName(c);
                const phone = c.telephone || c.mobile;
                return (
                  <tr key={c.id} style={{ opacity: c.status === 'active' ? 1 : 0.6 }}>
                    <td className="tc-title">
                      {/* the name opens the customer; its type tag and status travel with it as one unit */}
                      <button type="button" className="cust-name-btn" onClick={() => setDetailId(c.id)}>
                        <span>{name}</span>
                        <Pill kind="info">{c.customerType === 'company' ? 'Co' : 'Ind'}</Pill>
                        {c.status !== 'active' && <>{' '}<Pill kind="warn">{c.status}</Pill></>}
                      </button>
                    </td>
                    <td className={!c.companyName || c.companyName === name ? 'tc-hide' : undefined}>{c.companyName || '-'}</td>
                    <td className="tc-hide">{c.vatNumber || '-'}</td>
                    <td className="tc-hide">{c.email || '-'}</td>
                    <td className="tc-hide">{c.telephone || '-'}</td>
                    {/* the card's Call / Email buttons; the table keeps its Email and Telephone columns */}
                    <td className={`cust-contact${phone || c.email ? '' : ' tc-hide'}`}>
                      {phone && <a className="btn btn-sm" href={telHref(phone)} aria-label={`Call ${name}: ${phone}`}><span className="cust-contact-txt">Call {phone}</span></a>}
                      {c.email && <a className="btn btn-sm" href={`mailto:${c.email}`} aria-label={`Email ${name}: ${c.email}`}>Email</a>}
                    </td>
                    <td className="tc-hide">{c.country || '-'}</td>
                    <td className="tc-hide">{c.dealer || '-'}</td>
                    <td className="tc-hide">{c.orderCount}</td>
                    <td className="muted tc-hide">{fmtDate(c.lastOrderDate) || '-'}</td>
                    <td className="muted tc-hide">{fmtDate(c.updatedAt)}</td>
                    {/* the card's "VAT · country · dealer · orders · last order" line */}
                    <td className="tc-meta cust-meta">
                      {[
                        c.vatNumber && `VAT ${c.vatNumber}`,
                        c.country,
                        c.dealer,
                        `${c.orderCount} order${c.orderCount === 1 ? '' : 's'}`,
                        c.lastOrderDate && `last order ${fmtDate(c.lastOrderDate)}`,
                        `updated ${fmtDate(c.updatedAt)}`,
                      ].filter(Boolean).join(' · ')}
                    </td>
                    <td className={c.duplicateCount > 0 ? 'tc-end' : 'tc-hide'}>{c.duplicateCount > 0 && <span title="Possible duplicate"><Pill kind="warn"><IconAlertTriangle size={11} /> dup</Pill></span>}</td>
                    <td className="tc-actions cust-acts" style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn btn-sm" onClick={() => setDetailId(c.id)}>View</button>
                      {can('customer_database.edit') && c.status === 'active' && (
                        <button className="btn btn-sm btn-ghost" onClick={() => setEditId(c.id)}>Edit</button>
                      )}
                      {can('customer_database.delete') && (
                        c.status === 'active' ? (
                          <button className="btn btn-sm btn-ghost" disabled={archive.isPending} onClick={() => archive.mutate(c.id)}>Archive</button>
                        ) : (
                          <button className="btn btn-sm btn-ghost" disabled={restore.isPending} onClick={() => restore.mutate(c.id)}>Restore</button>
                        )
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {detailId != null && (
        <DetailModal
          id={detailId}
          onClose={() => setDetailId(null)}
          onEdit={() => { setEditId(detailId); setDetailId(null); }}
        />
      )}
      {editId !== undefined && (
        <CustomerFormModal
          initial={editingInitial}
          onClose={() => setEditId(undefined)}
          onSaved={(id) => setDetailId(id)}
        />
      )}
    </>
  );
}
